import { MDBIcon } from "mdb-react-ui-kit";
import React, { useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { PdfContext } from "../../context/pdf/pdf-context";
import { ResumeContext } from "../../context/resume/resume-context";
import { TranslationContext } from "../../context/translations/translations-context";
import { useCurrentUserRoles } from "../../hooks/useCurrentUserRoles";
import useResumeDocumentSection from "../../hooks/useResumeDocumentSection";
import useResumeProperties from "../../hooks/useResumeProperties";
import { generatePdfFileName } from "../../utils/generatePdfFileName";
import { cloneResume } from "../services/httpService";

export default function ResumePdfControls() {

    const { translations } = useContext( TranslationContext );
    const { resumeData } = useContext( ResumeContext );
    const {
        initiatePreviewTab,
        initiateDownloadModal,
        initiateShareModal,
        setPdfFileName,
        state: { pdfFileName },
    } = useContext( PdfContext );

    const { user } = useResumeDocumentSection();
    const { documentLanguage, keywords } = useResumeProperties();
    const { isClientRole, isManagerRole, isCandidateRole } = useCurrentUserRoles();
    const history = useHistory();

    const computedPdfName = useMemo(
        () => generatePdfFileName( user, documentLanguage, keywords ),
        [ user, documentLanguage, keywords ]
    );

    useEffect( () => {
        if ( computedPdfName && computedPdfName !== pdfFileName ) {
            setPdfFileName( computedPdfName );
        }
    }, [ computedPdfName, pdfFileName, setPdfFileName ] );

    async function handleDuplicate() {
        if ( !resumeData?.id || !resumeData?.userId ) {
            toast.error( "Impossible de dupliquer le CV : identifiants manquants." );
            return;
        }
        try {
            const response = await cloneResume( resumeData.id );
            if ( !response.ok ) {
                throw new Error( `HTTP error! status: ${ response.status }` );
            }
            const clonedResume = await response.json();
            toast.success( "CV dupliqué." );
            if ( clonedResume?.id ) {
                history.push( `/mon-espace-cv/${ resumeData.userId }/${ clonedResume.id }` );
            }
        } catch ( error ) {
            console.error( "Erreur lors de la duplication du CV :", error );
            toast.error( "Erreur lors de la duplication du CV." );
        }
    }

    return (
        <div className={ `row ${ isClientRole ? 'hide-for-client' : '' }` }>
            <div
                className={ isCandidateRole ? "col-6" : "col-12 col-md-3 col-lg-3" }>
                <div
                    className="text-underline d-flex gap-1 align-items-center pt-3 text-white justify-content-center"
                    style={ { cursor: "pointer" } }
                    onClick={ initiatePreviewTab }
                    role="button"
                >
                    <MDBIcon fas icon="eye" size="2x" style={ { color: "#09B4BF" } }/>
                    { ' ' }
                    { translations.pdf.previewButtonLabel }
                </div>
            </div>
            <div
                className={ isManagerRole ? "col-6" : "col-12 col-md-3 col-lg-3" }>
                <div className="text-underline pt-3 text-center text-white"
                     style={ { cursor: "pointer" } }
                     onClick={ handleDuplicate }
                     role="button"
                >
                    <MDBIcon
                        fas
                        icon="copy"
                        size="2x"
                        style={ { color: "#09B4BF" } }
                    />
                    { ' ' }
                    { translations.pdf.duplicateButtonLabel }
                </div>
            </div>
            { isManagerRole && (
                <>
                    <div className="col-12 col-md-3 col-lg-3">
                        <div className="text-underline pt-3 text-center text-white"
                             style={ { cursor: "pointer" } }
                             onClick={ initiateDownloadModal }
                             role="button"
                        >
                            <MDBIcon
                                fas
                                icon="cloud-download-alt"
                                size="2x"
                                style={ { color: "#09B4BF" } }
                            />
                            { ' ' }
                            { translations.pdf.downloadButtonLabel }
                        </div>
                    </div>
                    <div className="col-12 col-md-3 col-lg-3">
                        <div className="text-underline pt-3 text-center text-white"
                             style={ { cursor: "pointer" } }
                             onClick={ initiateShareModal }
                             role="button"
                        >
                            <MDBIcon
                                fas
                                icon="share-square"
                                size="2x"
                                style={ { color: "#09B4BF" } }
                            />{ " " }
                            { translations.pdf.shareButtonLabel }
                        </div>
                    </div>
                </>
            ) }
        </div>
    );
};
